import type { ReactNode } from "react";
import { ScreenIllustration, type ScreenIllustrationKind } from "@/components/screen-illustration";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Skeleton,
} from "@organizacaox/lai-design-system";

export type EmptyIllustration = ScreenIllustrationKind | BasicEmptyIllustration;

type BasicEmptyIllustration =
  | "content"
  | "learning"
  | "community"
  | "messages"
  | "people"
  | "search"
  | "achievement"
  | "settings";

export type SkeletonLayout = "list" | "cards" | "feed" | "conversation" | "form" | "lesson";

// Vetores de domínio: herdam as cores da marca e funcionam nos temas claro e escuro.
const illustrationPaths: Record<BasicEmptyIllustration, ReactNode> = {
  content: (
    <>
      <rect x="60" y="29" width="66" height="82" rx="8" />
      <path d="M76 49h34M76 62h34M76 75h22M76 94h14" />
      <path d="m126 44 14 6v65a7 7 0 0 1-7 7H80" />
    </>
  ),
  learning: (
    <>
      <path d="M100 48c-17-12-35-12-58-7v64c23-5 41-5 58 7 17-12 35-12 58-7V41c-23-5-41-5-58 7Zm0 0v64M54 57c11-2 22-1 32 4M54 72c11-2 22-1 32 4M114 61c10-5 21-6 32-4M114 76c10-5 21-6 32-4" />
    </>
  ),
  community: (
    <>
      <rect x="49" y="32" width="102" height="82" rx="10" />
      <circle cx="70" cy="53" r="7" />
      <path d="M86 50h46M86 59h27M64 78h72M64 89h56M64 101h35" />
      <path d="m137 100 8 8 15-17" />
    </>
  ),
  messages: (
    <>
      <path d="M53 32h78a9 9 0 0 1 9 9v42a9 9 0 0 1-9 9H83l-24 17V92h-6a9 9 0 0 1-9-9V41a9 9 0 0 1 9-9Z" />
      <path d="M140 55h8a9 9 0 0 1 9 9v38a9 9 0 0 1-9 9v13l-21-13h-22M64 53h55M64 68h36" />
    </>
  ),
  people: (
    <>
      <circle cx="100" cy="49" r="17" />
      <path d="M69 109V97a31 31 0 0 1 62 0v12H69Z" />
      <path d="M60 40a13 13 0 1 0 0 26M53 78c-12 2-19 13-19 26h22M140 40a13 13 0 1 1 0 26M147 78c12 2 19 13 19 26h-22" />
    </>
  ),
  search: (
    <>
      <rect x="45" y="30" width="83" height="82" rx="8" />
      <path d="M61 49h48M61 64h25M61 95h22" />
      <circle cx="119" cy="83" r="24" />
      <path d="m136 101 21 21M109 83h20" />
    </>
  ),
  achievement: (
    <>
      <rect x="43" y="34" width="114" height="74" rx="8" />
      <path d="M62 52h55M62 67h32M62 88h21" />
      <circle cx="125" cy="85" r="17" />
      <path d="m114 99-3 26 14-8 14 8-3-26m-18-42 5 5 9-10" />
    </>
  ),
  settings: (
    <>
      <rect x="48" y="32" width="104" height="80" rx="10" />
      <path d="M63 52h17m16 0h41M63 72h43m16 0h15M63 92h17m16 0h41" />
      <circle cx="88" cy="52" r="8" />
      <circle cx="114" cy="72" r="8" />
      <circle cx="88" cy="92" r="8" />
    </>
  ),
};

function isBasicIllustration(kind: EmptyIllustration): kind is BasicEmptyIllustration {
  return Object.hasOwn(illustrationPaths, kind);
}

export function PageEmpty({
  title,
  description,
  illustration = "content",
}: {
  title: string;
  description?: string;
  illustration?: EmptyIllustration;
}) {
  return (
    <Empty className="w-full min-w-0 rounded-xl border border-dashed bg-card py-8">
      <EmptyHeader>
        <EmptyMedia>
          {isBasicIllustration(illustration) ? (
            <svg
              viewBox="0 0 200 150"
              className="h-28 w-40 text-primary"
              aria-hidden="true"
              focusable="false"
            >
              <ellipse cx="100" cy="77" rx="79" ry="59" fill="currentColor" opacity="0.06" />
              <g
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <g opacity="0.3">
                  <path d="M25 48h10m-5-5v10M164 27h8m-4-4v8M160 125h10" />
                  <circle cx="31" cy="111" r="3" />
                </g>
                {illustrationPaths[illustration]}
              </g>
            </svg>
          ) : (
            <ScreenIllustration kind={illustration} className="h-auto w-56 max-w-full" />
          )}
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        {description ? <EmptyDescription>{description}</EmptyDescription> : null}
      </EmptyHeader>
    </Empty>
  );
}

function TextLines() {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <Skeleton className="h-4 w-2/3 motion-reduce:animate-none" />
      <Skeleton className="h-3 w-1/2 motion-reduce:animate-none" />
    </div>
  );
}

export function PageSkeleton({
  layout = "list",
  label = "Carregando conteúdo…",
}: {
  layout?: SkeletonLayout;
  label?: string;
}) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="w-full min-w-0">
      <span className="sr-only">{label}</span>
      <div
        aria-hidden="true"
        className={
          layout === "cards" ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3" : "flex flex-col gap-4"
        }
      >
        {layout === "lesson" ? (
          <>
            <Skeleton className="h-8 w-2/3 motion-reduce:animate-none" />
            <Skeleton className="aspect-video w-full motion-reduce:animate-none" />
            <TextLines />
          </>
        ) : layout === "form" ? (
          <div className="flex flex-col gap-6 rounded-xl border bg-card p-6">
            <Skeleton className="h-6 w-1/3 motion-reduce:animate-none" />
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex flex-col gap-2">
                <Skeleton className="h-3 w-24 motion-reduce:animate-none" />
                <Skeleton className="h-10 w-full motion-reduce:animate-none" />
              </div>
            ))}
          </div>
        ) : (
          [0, 1, 2].map((i) =>
            layout === "conversation" ? (
              <Skeleton
                key={i}
                className={`h-16 w-2/3 rounded-xl motion-reduce:animate-none ${i === 1 ? "self-end" : "self-start"}`}
              />
            ) : (
              <div key={i} className="flex flex-col gap-4 rounded-xl border bg-card p-4">
                {layout === "cards" ? (
                  <Skeleton className="aspect-video w-full motion-reduce:animate-none" />
                ) : null}
                <div className="flex items-center gap-3">
                  {layout !== "cards" ? (
                    <Skeleton
                      variant="circle"
                      className="size-10 shrink-0 motion-reduce:animate-none"
                    />
                  ) : null}
                  <TextLines />
                </div>
                {layout === "feed" ? (
                  <>
                    <Skeleton className="h-20 w-full motion-reduce:animate-none" />
                    <Skeleton className="h-4 w-24 motion-reduce:animate-none" />
                  </>
                ) : null}
              </div>
            ),
          )
        )}
      </div>
    </div>
  );
}
